"use client"
import { motion } from 'framer-motion';
import { Layers, Zap, PieChart, TrendingUp, Activity, Box } from 'lucide-react';

const PALETTES = [
    { bg: "bg-gradient-to-br from-[#0F172A] to-[#1E293B]", accent: "bg-blue-500", text: "text-white" }, // Slate
    { bg: "bg-gradient-to-br from-[#2E1065] to-[#4C1D95]", accent: "bg-violet-500", text: "text-white" }, // Deep Violet
    { bg: "bg-gradient-to-br from-[#064E3B] to-[#065F46]", accent: "bg-emerald-500", text: "text-white" }, // Emerald 
    { bg: "bg-gradient-to-br from-[#4A044E] to-[#701A75]", accent: "bg-pink-500", text: "text-white" }, // Magenta
    { bg: "bg-gradient-to-br from-[#172554] to-[#1E3A8A]", accent: "bg-sky-500", text: "text-white" } // Royal Blue
];

// More "Enterprise/SaaS" icons, less "Generic AI"
const ICONS = [Layers, PieChart, TrendingUp, Activity, Box, Zap];

interface CreativeCanvasProps {
    title?: string;
    category?: string;
    index?: number;
}

export const CreativeCanvas = ({ title = "Create Stunning Decks", category = "AI Powered", index = 0 }: CreativeCanvasProps) => {
    const palette = PALETTES[index % PALETTES.length];
    const Icon = ICONS[index % ICONS.length];

    // Split title
    const words = title.split(' ');
    // Highlight pattern: Highlight the *second* word usually, or middle
    const highlightIndex = words.length > 3 ? 2 : 1;

    return (
        <div className={`w-full h-full ${palette.bg} relative overflow-hidden flex flex-col p-6`}>

            {/* 1. Grain/Noise Texture for Premium Feel */}
            <div className="absolute inset-0 opacity-[0.15] mix-blend-overlay pointer-events-none"
                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")` }}
            />

            {/* 2. Abstract Blurry Orbs (Depth) */}
            <motion.div
                className={`absolute top-[-20%] right-[-10%] w-48 h-48 ${palette.accent} rounded-full blur-[60px] opacity-40`}
                animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
                transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.div
                className="absolute bottom-[-10%] left-[-10%] w-32 h-32 bg-white rounded-full blur-[50px] opacity-10"
                animate={{ x: [0, 20, 0] }}
                transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
            />

            {/* 3. Glassmorphism 'Card' Element (Abstract UI Mockup) */}
            <motion.div
                className="absolute top-6 right-6 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-3 shadow-xl transform rotate-12 z-10"
                initial={{ y: 0 }}
                whileHover={{ y: -5, rotate: 15 }}
                transition={{ duration: 0.3 }}
            >
                <Icon size={28} className="text-white/90 drop-shadow-md" strokeWidth={1.5} />
            </motion.div>

            {/* 4. Creative Scribble / Doodle (Hand-drawn effect) */}
            <div className="absolute bottom-16 right-4 opacity-40 transform -rotate-12 pointer-events-none">
                <svg width="60" height="40" viewBox="0 0 100 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <motion.path
                        d="M10 30 Q 30 10, 50 30 T 90 30"
                        stroke="white"
                        strokeWidth="3"
                        strokeLinecap="round"
                        fill="none"
                        initial={{ pathLength: 0 }}
                        whileInView={{ pathLength: 1 }}
                        transition={{ duration: 1.5, ease: "easeInOut" }}
                    />
                    <circle cx="85" cy="15" r="4" fill="white" className="animate-pulse" />
                    <circle cx="15" cy="45" r="3" fill="white" fillOpacity="0.5" />
                </svg>
            </div>

            {/* 5. Secondary Graphic Element (Grid or Lines) */}
            <div className="absolute top-1/2 right-0 w-24 h-24 opacity-20"
                style={{ backgroundImage: 'radial-gradient(white 1px, transparent 1px)', backgroundSize: '8px 8px' }}
            />

            {/* 5. Typography */}
            <div className="relative z-20 mt-auto">
                <span className="inline-flex items-center px-2.5 py-1 rounded-md text-[12px] font-bold bg-white/10 text-white/80 border border-white/10 backdrop-blur-sm mb-4">
                    {category}
                </span>

                <h3 className="font-syne font-bold text-[22px] leading-[1.15] text-white drop-shadow-md antialiased">
                    {words.map((word, i) => (
                        <span key={i} className="relative inline-block mr-1.5">
                            {i === highlightIndex ? (
                                <span className="relative">
                                    {/* Underline Highlight */}
                                    <span className={`absolute -bottom-1 left-0 right-0 h-3 ${palette.accent} -z-10 opacity-70 -rotate-1 rounded-sm blur-[1px]`} />
                                    <span className="relative z-10">{word}</span>
                                </span>
                            ) : (
                                <span className="text-white/95">{word}</span>
                            )}
                        </span>
                    ))}
                </h3>
            </div>
        </div>

    );
};

export default CreativeCanvas;
